import React, { useMemo, useState } from 'react';
import { AttributeDef, Product, ProductAttributeValue, ProductVariant } from '../types';
import { usePos } from '../context/PosContext';
import ProductImportExport from '../components/ProductImportExport';
import ProductHistoryModal from '../components/ProductHistoryModal';
import { collectBarcodes, nextEan13Batch } from '../services/ean';
import { printProductLabels } from '../services/labels';
import './Products.css';

// GST slabs + units mirroring the Android spinners (spinner_tax_index, spinner_unit, spinner_kitchen).
const GST_RATES = [0, 5, 12, 18, 28];
const UNITS = ['PCS', 'KG', 'GRAM', 'LTR', 'ML', 'BOX', 'PACK', 'DOZEN'];
const SERVICE_POINTS = ['Counter', 'Kitchen', 'Bar', 'Bakery'];

const blankForm = {
  name: '',
  regionalName: '',
  barcode: '',
  searchCode: '',
  shortName: '',
  hsnCode: '',
  category: '',
  subCategory: '',
  taxRatePercent: '5',
  cessPercent: '',
  unitGroupId: '',
  unit: 'PCS',
  servicePoint: 'Counter',
  weighable: false,
  stockable: true,
  stock: '',
  reorderLevel: '',
  batchNo: '',
  mfgDate: '',
  expDate: '',
  price: '',
  mrpPrice: '',
  discountPercent: '',
  discountAmt: '',
  price1: '',
  price2: '',
  price3: '',
  price4: '',
  price5: '',
  noOfPrint: '1',
};

const Products: React.FC = () => {
  const { products, categories: masterCategories, unitGroups, attributes, addProduct, updateProduct, deleteProduct, formatMoney, config, isOn } = usePos();
  const hideSearchCode = isOn('hide_product_search_code'); // "C - Product Search Code"
  const sortByName = isOn('product_sort_by_name'); // "K - Sort by Name"
  const productSyncOn = isOn('product_sync'); // "P - Product Sync"
  const [syncedAt, setSyncedAt] = useState<string | null>(null);

  // "P - Product Sync": the Android app pulls the product master from the
  // server. The web port is local-first, so Sync re-validates the local store
  // and confirms the count (and gives imports/exports a one-tap refresh point).
  const syncProducts = () => {
    setSyncedAt(new Date().toLocaleTimeString());
    alert(`Product sync complete — ${products.length} products up to date.`);
  };
  const [editing, setEditing] = useState<Product | null>(null);
  const [listSearch, setListSearch] = useState('');
  const [f, setF] = useState({ ...blankForm });
  const [variants, setVariants] = useState<ProductVariant[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  // Attribute Settings — picked values keyed by attribute id. Single-value
  // attributes keep a one-element array so both shapes share one code path.
  const [attrValues, setAttrValues] = useState<Record<string, string[]>>({});
  // List filter: attributeId -> value (only `filterable` list attributes appear).
  const [attrFilter, setAttrFilter] = useState<Record<string, string>>({});

  const set = (k: keyof typeof blankForm, v: string | boolean) => setF((prev) => ({ ...prev, [k]: v }));

  const addVariant = () =>
    setVariants((prev) => [...prev, { id: Date.now().toString(), name: '', price: 0, inStock: true }]);
  const NUMERIC_VARIANT_FIELDS: (keyof ProductVariant)[] = ['price', 'stock', 'costPrice'];
  const setVariant = (id: string, k: keyof ProductVariant, v: string | boolean) =>
    setVariants((prev) =>
      prev.map((x) =>
        x.id === id
          ? { ...x, [k]: NUMERIC_VARIANT_FIELDS.includes(k) ? parseFloat(v as string) || 0 : v }
          : x
      )
    );

  // Issue a barcode for one variant that hasn't got one (or re-issue on demand).
  const assignVariantBarcode = (id: string) =>
    setVariants((prev) => {
      const taken = [
        ...collectBarcodes(products),
        f.barcode,
        ...prev.filter((v) => v.id !== id).map((v) => v.barcode ?? ''),
      ].filter(Boolean) as string[];
      const [code] = nextEan13Batch(taken, 1);
      return prev.map((v) => (v.id === id ? { ...v, barcode: code } : v));
    });
  const delVariant = (id: string) => setVariants((prev) => prev.filter((x) => x.id !== id));

  // ---- Attribute Settings -------------------------------------------------
  // Attributes offered for the product being edited: the active ones, limited to
  // those that apply to the chosen category (an attribute with no category list
  // applies everywhere).
  const activeAttributes = useMemo(
    () =>
      [...attributes]
        .filter((a) => a.active !== false)
        .sort((a, b) => (a.serialNo ?? 9999) - (b.serialNo ?? 9999) || a.name.localeCompare(b.name)),
    [attributes]
  );

  const formAttributes = useMemo(
    () =>
      activeAttributes.filter(
        (a) => !a.categories?.length || (!!f.category && a.categories.includes(f.category))
      ),
    [activeAttributes, f.category]
  );

  const attrOptions = (a: AttributeDef) =>
    [...a.options]
      .filter((o) => o.active !== false)
      .sort((x, y) => (x.serialNo ?? 9999) - (y.serialNo ?? 9999));

  const getAttr = (id: string) => attrValues[id] ?? [];

  const setAttr = (id: string, values: string[]) =>
    setAttrValues((prev) => {
      const kept = values.filter((v) => v !== '');
      const next = { ...prev };
      if (kept.length === 0) delete next[id];
      else next[id] = kept;
      return next;
    });

  const toggleAttrValue = (id: string, value: string) => {
    const cur = getAttr(id);
    setAttr(id, cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value]);
  };

  // Variant axes = variant-flagged attributes with at least one value picked on
  // this product. The cartesian product of those picks becomes the variant
  // matrix; each variant's price is the base price plus the extra price of every
  // option in its combination.
  const variantAxes = useMemo(
    () =>
      formAttributes.filter(
        (a) => a.useForVariants && a.inputType === 'list' && (attrValues[a.id]?.length ?? 0) > 0
      ),
    [formAttributes, attrValues]
  );

  const sameCombo = (a: Record<string, string>, b?: Record<string, string>) =>
    !!b &&
    Object.keys(a).length === Object.keys(b).length &&
    Object.keys(a).every((k) => a[k] === b[k]);

  const generateVariants = () => {
    if (variantAxes.length === 0) {
      alert('Pick values for at least one "Use for Variants" attribute first.');
      return;
    }
    const base = parseFloat(f.price) || 0;
    let combos: { values: Record<string, string>; extra: number; label: string[] }[] = [
      { values: {}, extra: 0, label: [] },
    ];
    variantAxes.forEach((a) => {
      const picks = attrValues[a.id] ?? [];
      combos = combos.flatMap((c) =>
        picks.map((v) => {
          const opt = a.options.find((o) => o.value === v);
          return {
            values: { ...c.values, [a.id]: v },
            extra: c.extra + (opt?.extraPrice ?? 0),
            label: [...c.label, v],
          };
        })
      );
    });
    if (combos.length > 200) {
      alert(`That would create ${combos.length} variants. Narrow the selection down first.`);
      return;
    }

    setVariants((prev) => {
      // Keep manually added rows and any generated row whose combination is
      // still in the matrix (so edited prices, stock and printed barcodes
      // survive a re-generate); append the combinations that are new.
      const kept = prev.filter(
        (v) => !v.attributeValues || combos.some((c) => sameCombo(c.values, v.attributeValues))
      );
      const fresh = combos.filter((c) => !kept.some((v) => sameCombo(c.values, v.attributeValues)));
      // New codes avoid every barcode already in the master, including the ones
      // on rows we just kept but haven't saved yet. Kept rows that never got a
      // code (variants added before labels existed) are backfilled here too, so
      // one press leaves every row scannable.
      const needBackfill = kept.filter((v) => !v.barcode);
      const codes = nextEan13Batch(
        [...collectBarcodes(products), f.barcode, ...kept.map((v) => v.barcode ?? '')].filter(
          Boolean
        ) as string[],
        fresh.length + needBackfill.length
      );
      const backfilled = kept.map((v) => {
        const idx = needBackfill.indexOf(v);
        return idx < 0 ? v : { ...v, barcode: codes[fresh.length + idx] };
      });
      const added = fresh.map((c, i) => ({
        id: `${Date.now()}-${i}`,
        name: c.label.join(' / '),
        price: +(base + c.extra).toFixed(2),
        inStock: true,
        stock: 0,
        attributeValues: c.values,
        barcode: codes[i],
        sku: [f.shortName || f.name, ...c.label]
          .filter(Boolean)
          .join('-')
          .toUpperCase()
          .replace(/\s+/g, ''),
      }));
      return [...backfilled, ...added];
    });
  };

  // Re-price the generated variants from the current base price + option extras.
  const repriceVariants = () => {
    const base = parseFloat(f.price) || 0;
    setVariants((prev) =>
      prev.map((v) => {
        if (!v.attributeValues) return v;
        const extra = Object.entries(v.attributeValues).reduce((sum, [attrId, val]) => {
          const opt = attributes.find((a) => a.id === attrId)?.options.find((o) => o.value === val);
          return sum + (opt?.extraPrice ?? 0);
        }, 0);
        return { ...v, price: +(base + extra).toFixed(2) };
      })
    );
  };

  // Non-null only while the product being edited actually has variants.
  const variantStockTotal = variants.filter((v) => v.name.trim()).length
    ? variants.filter((v) => v.name.trim()).reduce((sum, v) => sum + (v.stock ?? 0), 0)
    : null;

  const buildAttributes = (): ProductAttributeValue[] =>
    formAttributes
      .filter((a) => (attrValues[a.id]?.length ?? 0) > 0)
      .map((a) => ({ attributeId: a.id, name: a.name, values: attrValues[a.id] }));

  // Attributes marked `required` that the product has left empty.
  const missingRequired = () =>
    formAttributes.filter((a) => a.required && (attrValues[a.id]?.length ?? 0) === 0).map((a) => a.name);

  // Category dropdown sources the top-level Category Master (in serial order)
  // plus any names already on products that aren't in the master yet.
  const categories = useMemo(() => {
    const master = [...masterCategories]
      .filter((c) => !c.parentCategory)
      .sort((a, b) => (a.serialNo ?? 9999) - (b.serialNo ?? 9999) || a.name.localeCompare(b.name))
      .map((c) => c.name);
    const s = new Set(master);
    const extras: string[] = [];
    products.forEach((p) => {
      if (p.category && !s.has(p.category)) {
        s.add(p.category);
        extras.push(p.category);
      }
    });
    return [...master, ...extras.sort()];
  }, [products, masterCategories]);

  // Sub-category dropdown: the Category Master rows whose parent is the selected
  // category, plus any sub-category names already on products in that category.
  const subCategories = useMemo(() => {
    if (!f.category) return [];
    const master = [...masterCategories]
      .filter((c) => c.parentCategory === f.category)
      .sort((a, b) => (a.serialNo ?? 9999) - (b.serialNo ?? 9999) || a.name.localeCompare(b.name))
      .map((c) => c.name);
    const s = new Set(master);
    const extras: string[] = [];
    products.forEach((p) => {
      if (p.category === f.category && p.subCategory && !s.has(p.subCategory)) {
        s.add(p.subCategory);
        extras.push(p.subCategory);
      }
    });
    return [...master, ...extras.sort()];
  }, [products, masterCategories, f.category]);

  // Attributes offered as list filters above the product list.
  const filterableAttributes = useMemo(
    () =>
      activeAttributes.filter(
        (a) => a.filterable !== false && a.inputType === 'list' && a.options.length > 0
      ),
    [activeAttributes]
  );

  const filtered = useMemo(() => {
    const t = listSearch.trim().toLowerCase();
    let base = !t
      ? products
      : products.filter((p) => p.name.toLowerCase().includes(t) || p.barcode?.includes(listSearch) || p.hsnCode?.includes(listSearch));
    const active = Object.entries(attrFilter).filter(([, v]) => v);
    if (active.length) {
      base = base.filter((p) =>
        active.every(([attrId, val]) =>
          p.attributes?.some((a) => a.attributeId === attrId && a.values.includes(val))
        )
      );
    }
    return sortByName ? [...base].sort((a, b) => a.name.localeCompare(b.name)) : base;
  }, [products, listSearch, sortByName, attrFilter]);

  const reset = () => {
    setF({ ...blankForm });
    setVariants([]);
    setAttrValues({});
    setEditing(null);
  };

  const buildData = () => ({
    name: f.name,
    regionalName: f.regionalName || undefined,
    barcode: f.barcode,
    searchCode: f.searchCode || undefined,
    shortName: f.shortName || undefined,
    hsnCode: f.hsnCode || undefined,
    category: f.category,
    subCategory: f.category && f.subCategory ? f.subCategory : undefined,
    taxRatePercent: f.taxRatePercent ? parseFloat(f.taxRatePercent) : undefined,
    cessPercent: f.cessPercent ? parseFloat(f.cessPercent) : undefined,
    unitGroupId: f.unitGroupId || undefined,
    unit: f.unit,
    servicePoint: f.servicePoint,
    weighable: f.weighable,
    stockable: f.stockable,
    // For a variant product the parent count is the sum of the variants, so the
    // Stock Details box and the variant rows can never disagree.
    stock: variants.filter((v) => v.name.trim()).length
      ? variants.filter((v) => v.name.trim()).reduce((sum, v) => sum + (v.stock ?? 0), 0)
      : parseFloat(f.stock) || 0,
    reorderLevel: f.reorderLevel ? parseFloat(f.reorderLevel) : undefined,
    batchNo: f.batchNo || undefined,
    mfgDate: f.mfgDate || undefined,
    expDate: f.expDate || undefined,
    price: parseFloat(f.price) || 0,
    mrpPrice: f.mrpPrice ? parseFloat(f.mrpPrice) : undefined,
    discountPercent: f.discountPercent ? parseFloat(f.discountPercent) : undefined,
    priceTiers: [f.price1, f.price2, f.price3, f.price4, f.price5].map((x) => parseFloat(x) || 0),
    variants: variants.filter((v) => v.name.trim()).length
      ? variants.filter((v) => v.name.trim())
      : undefined,
    attributes: buildAttributes().length ? buildAttributes() : undefined,
  });

  const save = () => {
    if (!f.name || !f.price) {
      alert('Name and Selling Price are required.');
      return;
    }
    const missing = missingRequired();
    if (missing.length) {
      alert(`These attributes are required: ${missing.join(', ')}`);
      return;
    }
    if (editing) updateProduct({ ...editing, ...buildData() });
    else addProduct(buildData());
    reset();
  };

  const edit = (p: Product) => {
    setEditing(p);
    const t = p.priceTiers ?? [];
    setF({
      name: p.name,
      regionalName: p.regionalName ?? '',
      barcode: p.barcode ?? '',
      searchCode: p.searchCode ?? '',
      shortName: p.shortName ?? '',
      hsnCode: p.hsnCode ?? '',
      category: p.category ?? '',
      subCategory: p.subCategory ?? '',
      taxRatePercent: String(p.taxRatePercent ?? config.taxRatePercent),
      cessPercent: p.cessPercent != null ? String(p.cessPercent) : '',
      unitGroupId: p.unitGroupId ?? '',
      unit: p.unit ?? 'PCS',
      servicePoint: p.servicePoint ?? 'Counter',
      weighable: p.weighable ?? false,
      stockable: p.stockable ?? true,
      stock: String(p.stock),
      reorderLevel: p.reorderLevel != null ? String(p.reorderLevel) : '',
      batchNo: p.batchNo ?? '',
      mfgDate: p.mfgDate ?? '',
      expDate: p.expDate ?? '',
      price: String(p.price),
      mrpPrice: p.mrpPrice != null ? String(p.mrpPrice) : '',
      discountPercent: p.discountPercent != null ? String(p.discountPercent) : '',
      discountAmt: '',
      price1: t[0] != null ? String(t[0]) : '',
      price2: t[1] != null ? String(t[1]) : '',
      price3: t[2] != null ? String(t[2]) : '',
      price4: t[3] != null ? String(t[3]) : '',
      price5: t[4] != null ? String(t[4]) : '',
      noOfPrint: '1',
    });
    setVariants(p.variants ? p.variants.map((v) => ({ ...v })) : []);
    const vals: Record<string, string[]> = {};
    (p.attributes ?? []).forEach((a) => {
      if (a.values.length) vals[a.attributeId] = [...a.values];
    });
    setAttrValues(vals);
  };

  // Print labels for the saved product: one batch per variant when it has them
  // (each carrying that variant's own barcode and price), else the product's
  // own code. Falls back to the browser print sheet when no label printer is
  // configured — same path the Barcode Printing screen uses.
  const printBarcodeLabels = async () => {
    if (!editing) return;
    const copies = Math.max(1, parseInt(f.noOfPrint, 10) || 1);
    const rows = editing.variants?.length
      ? editing.variants.map((v) => ({
          name: `${editing.name} · ${v.name}`,
          code: v.barcode || editing.barcode || String(editing.id),
          price: v.price,
        }))
      : [{ name: editing.name, code: editing.barcode || String(editing.id), price: editing.price }];
    const missing = rows.filter((r) => !r.code);
    if (missing.length) {
      alert('This product has no barcode yet — generate one on the variant rows first.');
      return;
    }
    await printProductLabels(rows, copies, config.storeName, formatMoney);
  };

  const remove = () => {
    if (editing && window.confirm('Delete this product?')) {
      deleteProduct(editing.id);
      reset();
    }
  };

  const lbl = (text: string) => <label className="pf-lbl">{text}</label>;

  return (
    <div className="pm-page">
      {/* LEFT: product list */}
      <div className="pm-list card-rounded">
        <input className="pm-list-search" placeholder="Search Product" value={listSearch} onChange={(e) => setListSearch(e.target.value)} />
        {filterableAttributes.length > 0 && (
          <div className="pm-attr-filters">
            {filterableAttributes.map((a) => (
              <select
                key={a.id}
                value={attrFilter[a.id] ?? ''}
                onChange={(e) => setAttrFilter((prev) => ({ ...prev, [a.id]: e.target.value }))}
                title={`Filter by ${a.name}`}
              >
                <option value="">{a.name}: All</option>
                {attrOptions(a).map((o) => (
                  <option key={o.id} value={o.value}>
                    {o.value}
                  </option>
                ))}
              </select>
            ))}
            {Object.values(attrFilter).some(Boolean) && (
              <button className="pf-btn-grey" onClick={() => setAttrFilter({})}>
                Clear
              </button>
            )}
          </div>
        )}
        <div className="pm-list-scroll">
          {filtered.map((p) => (
            <button key={p.id} className={`pm-list-item ${editing?.id === p.id ? 'sel' : ''}`} onClick={() => edit(p)}>
              <span className="pm-li-name">{p.name}</span>
              <span className="pm-li-meta">
                {formatMoney(p.price)} · {p.category || '—'}{p.subCategory ? ` › ${p.subCategory}` : ''} · stk {p.stock}
              </span>
            </button>
          ))}
          {filtered.length === 0 && <p className="muted" style={{ padding: 16 }}>No products.</p>}
        </div>
      </div>

      {/* RIGHT: add/edit form matching product_add_frag_lay.xml */}
      <div className="pm-form card-rounded">
        <div className="pf-top">
          <input className="pf-top-search" placeholder="Search Product" value={listSearch} onChange={(e) => setListSearch(e.target.value)} />
          <div className="pf-top-btns">
            {productSyncOn && (
              <button className="pf-btn-teal" onClick={syncProducts} title={syncedAt ? `Last sync ${syncedAt}` : 'Sync product master'}>
                {syncedAt ? `Synced ${syncedAt}` : 'Sync'}
              </button>
            )}
            <ProductImportExport exportClass="pf-btn-grey" importClass="pf-btn-teal" />
            <button
              className="pf-btn-grey"
              onClick={() => setShowHistory(true)}
              title={editing ? `Change history of ${editing.name}` : 'Recent changes across all products'}
            >
              History
            </button>
            {editing && (
              <button className="pf-btn-orange" onClick={remove}>
                Delete
              </button>
            )}
            <button className="pf-btn-orange" onClick={save}>
              {editing ? 'Update' : 'Save'}
            </button>
          </div>
        </div>

        <div className="pf-scroll">
          {/* Name | Regional Name | Barcode */}
          <div className="pf-row r3">
            <div>{lbl('Name *')}<input value={f.name} onChange={(e) => set('name', e.target.value)} /></div>
            <div>{lbl('Regional Name')}<input value={f.regionalName} onChange={(e) => set('regionalName', e.target.value)} /></div>
            <div>{lbl('Barcode')}<input value={f.barcode} onChange={(e) => set('barcode', e.target.value)} /></div>
          </div>

          {/* Category | GST Rates | Units | Service Point */}
          <div className="pf-row r4">
            <div>
              {lbl('Category')}
              <select
                value={f.category}
                onChange={(e) => {
                  // Switching category invalidates the previously picked sub-category.
                  set('category', e.target.value);
                  set('subCategory', '');
                }}
              >
                <option value="">— None —</option>
                {/* keep a legacy value selectable even if it's gone from the master */}
                {f.category && !categories.includes(f.category) && (
                  <option value={f.category}>{f.category}</option>
                )}
                {categories.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
            <div>
              {lbl('Sub Category')}
              <select
                value={f.subCategory}
                onChange={(e) => set('subCategory', e.target.value)}
                disabled={!f.category}
                title={f.category ? '' : 'Select a category first'}
              >
                <option value="">— None —</option>
                {/* keep a legacy value selectable even if it's gone from the master */}
                {f.subCategory && !subCategories.includes(f.subCategory) && (
                  <option value={f.subCategory}>{f.subCategory}</option>
                )}
                {subCategories.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
            <div>
              {lbl('GST Rates')}
              <select value={f.taxRatePercent} onChange={(e) => set('taxRatePercent', e.target.value)}>
                {GST_RATES.map((r) => (
                  <option key={r} value={r}>
                    {r}%
                  </option>
                ))}
              </select>
            </div>
            <div>
              {lbl('CESS %')}
              <input type="number" value={f.cessPercent} placeholder="0" onChange={(e) => set('cessPercent', e.target.value)} />
            </div>
            <div>
              {lbl('Unit Group')}
              <select value={f.unitGroupId} onChange={(e) => set('unitGroupId', e.target.value)}>
                <option value="">— None —</option>
                {unitGroups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              {lbl('Units')}
              <select value={f.unit} onChange={(e) => set('unit', e.target.value)}>
                {(() => {
                  const grp = unitGroups.find((g) => g.id === f.unitGroupId);
                  const opts = grp ? grp.units.map((u) => u.name) : UNITS;
                  return opts.map((u) => (
                    <option key={u} value={u}>
                      {u}
                    </option>
                  ));
                })()}
              </select>
            </div>
            <div>
              {lbl('Service Point')}
              <select value={f.servicePoint} onChange={(e) => set('servicePoint', e.target.value)}>
                {SERVICE_POINTS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Search Code | short name | HSN Code */}
          <div className="pf-row r3">
            {!hideSearchCode && (
              <div>{lbl('Search Code')}<input value={f.searchCode} onChange={(e) => set('searchCode', e.target.value)} /></div>
            )}
            <div>{lbl('short name')}<input value={f.shortName} onChange={(e) => set('shortName', e.target.value)} /></div>
            <div>{lbl('HSN Code')}<input value={f.hsnCode} onChange={(e) => set('hsnCode', e.target.value)} /></div>
          </div>

          {/* Weighable | Stockable */}
          <div className="pf-checks">
            <label>
              <input type="checkbox" checked={f.weighable} onChange={(e) => set('weighable', e.target.checked)} /> Weighable
            </label>
            <label>
              <input type="checkbox" checked={f.stockable} onChange={(e) => set('stockable', e.target.checked)} /> Stockable
            </label>
          </div>

          {/* Stock Details */}
          <div className="pf-section">Stock Details</div>
          <div className="pf-row r3">
            <div>
              {lbl(variantStockTotal != null ? 'Current Stock (sum of variants)' : 'Current Stock')}
              <input
                type="number"
                value={variantStockTotal != null ? variantStockTotal : f.stock}
                readOnly={variantStockTotal != null}
                title={variantStockTotal != null ? 'Set stock per variant below, or receive it on Goods Received' : ''}
                onChange={(e) => set('stock', e.target.value)}
              />
            </div>
            <div>{lbl('Reorder Level')}<input type="number" value={f.reorderLevel} onChange={(e) => set('reorderLevel', e.target.value)} /></div>
            <div>{lbl('Batch No')}<input value={f.batchNo} onChange={(e) => set('batchNo', e.target.value)} /></div>
          </div>
          <div className="pf-row r3">
            <div>{lbl('Mfg Date')}<input type="date" value={f.mfgDate} onChange={(e) => set('mfgDate', e.target.value)} /></div>
            <div>{lbl('Expiry Date')}<input type="date" value={f.expDate} onChange={(e) => set('expDate', e.target.value)} /></div>
            <div />
          </div>

          {/* Attribute Settings — values from the Attribute Master */}
          <div className="pf-section">
            Attribute Settings
            {formAttributes.length > 0 && (
              <span className="pf-section-note">
                {' '}
                · {Object.keys(attrValues).length} of {formAttributes.length} set
              </span>
            )}
          </div>
          {formAttributes.length === 0 ? (
            <p className="muted" style={{ margin: '4px 0 8px' }}>
              No attributes apply to this product. Define them in Inventory → Attribute Master
              {f.category ? ` (or allow them for the "${f.category}" category)` : ''}.
            </p>
          ) : (
            <>
              <div className="pf-row r3">
                {formAttributes.map((a) => (
                  <div key={a.id}>
                    {lbl(`${a.name}${a.required ? ' *' : ''}${a.useForVariants ? ' ⟡' : ''}`)}
                    {a.inputType === 'list' && a.multiSelect && (
                      <div className="pf-attr-multi">
                        {attrOptions(a).map((o) => (
                          <label key={o.id} className="pf-check">
                            <input
                              type="checkbox"
                              checked={getAttr(a.id).includes(o.value)}
                              onChange={() => toggleAttrValue(a.id, o.value)}
                            />{' '}
                            {o.value}
                            {o.extraPrice ? ` (+${o.extraPrice})` : ''}
                          </label>
                        ))}
                      </div>
                    )}
                    {a.inputType === 'list' && !a.multiSelect && !a.useForVariants && (
                      <select value={getAttr(a.id)[0] ?? ''} onChange={(e) => setAttr(a.id, [e.target.value])}>
                        <option value="">— None —</option>
                        {/* keep a legacy value selectable even if it left the master */}
                        {getAttr(a.id)[0] && !a.options.some((o) => o.value === getAttr(a.id)[0]) && (
                          <option value={getAttr(a.id)[0]}>{getAttr(a.id)[0]}</option>
                        )}
                        {attrOptions(a).map((o) => (
                          <option key={o.id} value={o.value}>
                            {o.value}
                            {o.extraPrice ? ` (+${o.extraPrice})` : ''}
                          </option>
                        ))}
                      </select>
                    )}
                    {/* variant axes take several picks — each becomes a variant */}
                    {a.inputType === 'list' && !a.multiSelect && a.useForVariants && (
                      <div className="pf-attr-multi">
                        {attrOptions(a).map((o) => (
                          <label key={o.id} className="pf-check">
                            <input
                              type="checkbox"
                              checked={getAttr(a.id).includes(o.value)}
                              onChange={() => toggleAttrValue(a.id, o.value)}
                            />{' '}
                            {o.value}
                            {o.extraPrice ? ` (+${o.extraPrice})` : ''}
                          </label>
                        ))}
                      </div>
                    )}
                    {a.inputType === 'text' && (
                      <input value={getAttr(a.id)[0] ?? ''} onChange={(e) => setAttr(a.id, [e.target.value])} />
                    )}
                    {a.inputType === 'number' && (
                      <input
                        type="number"
                        value={getAttr(a.id)[0] ?? ''}
                        onChange={(e) => setAttr(a.id, [e.target.value])}
                      />
                    )}
                    {a.inputType === 'date' && (
                      <input
                        type="date"
                        value={getAttr(a.id)[0] ?? ''}
                        onChange={(e) => setAttr(a.id, [e.target.value])}
                      />
                    )}
                    {a.inputType === 'boolean' && (
                      <select value={getAttr(a.id)[0] ?? ''} onChange={(e) => setAttr(a.id, [e.target.value])}>
                        <option value="">— None —</option>
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                      </select>
                    )}
                  </div>
                ))}
              </div>
              <div className="pf-attr-actions">
                <button
                  className="pf-btn-teal"
                  onClick={generateVariants}
                  disabled={variantAxes.length === 0}
                  title="Build the variant matrix from the ⟡ attributes"
                >
                  Generate Variants
                </button>
                <button className="pf-btn-grey" onClick={repriceVariants}>
                  Re-price from Attributes
                </button>
                <button className="pf-btn-grey" onClick={() => setAttrValues({})}>
                  Clear Attributes
                </button>
                {variantAxes.length > 0 && (
                  <span className="muted">
                    {variantAxes.map((a) => `${a.name} (${attrValues[a.id].length})`).join(' × ')} ={' '}
                    {variantAxes.reduce((n, a) => n * attrValues[a.id].length, 1)} variants
                  </span>
                )}
              </div>
            </>
          )}

          {/* Variants */}
          <div className="pf-section">Variants</div>
          {variants.map((v) => (
            <div className="pf-row r5" key={v.id} style={{ alignItems: 'end' }}>
              <div>{lbl('Name')}<input value={v.name} onChange={(e) => setVariant(v.id, 'name', e.target.value)} placeholder="e.g. Large" /></div>
              <div>{lbl('Price')}<input type="number" value={v.price} onChange={(e) => setVariant(v.id, 'price', e.target.value)} /></div>
              <div>{lbl('Stock')}<input type="number" value={v.stock ?? 0} onChange={(e) => setVariant(v.id, 'stock', e.target.value)} /></div>
              <div>
                {lbl('Barcode')}
                <div className="pf-var-code">
                  <input
                    value={v.barcode ?? ''}
                    onChange={(e) => setVariant(v.id, 'barcode', e.target.value)}
                    placeholder="EAN-13"
                  />
                  <button
                    className="pf-btn-grey"
                    onClick={() => assignVariantBarcode(v.id)}
                    title="Issue a fresh EAN-13 for this variant"
                  >
                    ⟳
                  </button>
                </div>
              </div>
              <div className="pf-var-meta">
                <label className="pf-check"><input type="checkbox" checked={!!v.isDefault} onChange={(e) => setVariant(v.id, 'isDefault', e.target.checked)} /> Default</label>
                <button className="pf-btn-grey" onClick={() => delVariant(v.id)}>Remove</button>
                {v.sku && <span className="pf-var-sku" title={`SKU: ${v.sku}`}>{v.sku}</span>}
              </div>
            </div>
          ))}
          <button className="pf-btn-orange" onClick={addVariant} style={{ marginTop: 4 }}>+ Add Variant</button>

          {/* selling Price | MRP | Discount % | Discount Amt */}
          <div className="pf-row r4">
            <div>{lbl('selling Price *')}<input type="number" value={f.price} onChange={(e) => set('price', e.target.value)} /></div>
            <div>{lbl('MRP Price')}<input type="number" value={f.mrpPrice} onChange={(e) => set('mrpPrice', e.target.value)} /></div>
            <div>{lbl('Discount ( % )')}<input type="number" value={f.discountPercent} onChange={(e) => set('discountPercent', e.target.value)} /></div>
            <div>{lbl('Discount Amt')}<input type="number" value={f.discountAmt} onChange={(e) => set('discountAmt', e.target.value)} /></div>
          </div>

          {/* Price 1-5 */}
          <div className="pf-row r5">
            <div>{lbl('Price 1')}<input type="number" value={f.price1} onChange={(e) => set('price1', e.target.value)} /></div>
            <div>{lbl('Price 2')}<input type="number" value={f.price2} onChange={(e) => set('price2', e.target.value)} /></div>
            <div>{lbl('Price 3')}<input type="number" value={f.price3} onChange={(e) => set('price3', e.target.value)} /></div>
            <div>{lbl('Price 4')}<input type="number" value={f.price4} onChange={(e) => set('price4', e.target.value)} /></div>
            <div>{lbl('Price 5')}<input type="number" value={f.price5} onChange={(e) => set('price5', e.target.value)} /></div>
          </div>

          {/* Print Barcode */}
          <div className="pf-barcode">
            {lbl('No. of Print')}
            <input className="pf-print-count" type="number" value={f.noOfPrint} onChange={(e) => set('noOfPrint', e.target.value)} />
            <button
              className="pf-btn-teal"
              onClick={printBarcodeLabels}
              disabled={!editing}
              title={editing ? 'Print labels for this product (one per variant when it has them)' : 'Save the product first'}
            >
              Print Barcode
            </button>
            {editing && (
              <button className="pf-btn-grey" onClick={reset}>
                New
              </button>
            )}
          </div>
        </div>
      </div>

      {showHistory && <ProductHistoryModal product={editing} onClose={() => setShowHistory(false)} />}
    </div>
  );
};

export default Products;
